// frontend/src/features/equipment/EquipmentSetupWizard/EquipmentSetupWizard.tsx
import { useEffect, useMemo, useState } from 'react';
import { Modal, Stepper, Button, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useNavigate } from 'react-router-dom';
import { WizardProvider, useWizard } from './WizardContext';
import { useSaveEquipment } from './hooks/useSaveEquipment';
import { useEquipmentList } from '@/hooks/useEquipment';
import {
  getCategoryForSubcategory,
  getWizardCategoryForEquipment,
  pendingToCreateBatch,
} from './utils/equipmentMapping';
import {
  WelcomeStep,
  CirculationStep,
  LightingStep,
  FiltrationStep,
  ControlStep,
  OtherStep,
  ReviewStep,
  CompletionStep,
} from './steps';
import { WIZARD_STEPS } from './types';

interface EquipmentSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

/**
 * Internal wizard content component that uses the context.
 */
function WizardContent({
  isOpen,
  onClose,
  onComplete,
}: {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}) {
  const navigate = useNavigate();
  const {
    state,
    nextStep,
    prevStep,
    skipCategory,
    setEquipment,
    removePendingByTempIds,
    setPreexistingCategories,
    markCategorySaved,
    addSessionSavedEquipment,
    isFirstStep,
    isLastStep,
    currentStepInfo,
  } = useWizard();
  const { data: equipmentData } = useEquipmentList();

  const {
    saveEquipment,
    reset: resetSave,
    isSaving,
  } = useSaveEquipment();

  // Track if we're showing the completion step
  const [showCompletion, setShowCompletion] = useState(false);
  const currentCategoryId = currentStepInfo.categoryId;

  const preexistingCategories = useMemo(() => {
    const categories = new Set<string>();
    for (const equipment of equipmentData?.equipment ?? []) {
      const category = getWizardCategoryForEquipment(equipment);
      if (category) {
        categories.add(category);
      }
    }
    return [...categories];
  }, [equipmentData?.equipment]);

  useEffect(() => {
    const hasChanged =
      preexistingCategories.length !== state.preexistingCategories.length ||
      preexistingCategories.some(
        (category) => !state.preexistingCategories.includes(category)
      );
    if (hasChanged) {
      setPreexistingCategories(preexistingCategories);
    }
  }, [preexistingCategories, setPreexistingCategories, state.preexistingCategories]);

  const getCategoryDrafts = (categoryId: string) =>
    state.pendingEquipment.filter(
      (equipment) => getCategoryForSubcategory(equipment.subcategoryId) === categoryId
    );

  const currentCategoryDrafts = currentCategoryId
    ? getCategoryDrafts(currentCategoryId)
    : [];

  const completionSavedCount = useMemo(() => {
    try {
      return pendingToCreateBatch(state.sessionSavedEquipment).length;
    } catch (err) {
      console.warn('Failed to compute batch count, falling back to item count:', err);
      return state.sessionSavedEquipment.length;
    }
  }, [state.sessionSavedEquipment]);

  const handleNext = async () => {
    if (!currentCategoryId) {
      nextStep();
      return;
    }

    const categoryDrafts = getCategoryDrafts(currentCategoryId);
    if (categoryDrafts.length === 0) {
      markCategorySaved(currentCategoryId);
      nextStep();
      return;
    }

    try {
      const result = await saveEquipment(categoryDrafts);
      if (result.error) {
        return;
      }

      const savedDrafts = categoryDrafts.filter((equipment) =>
        result.savedTempIds.includes(equipment.tempId)
      );
      addSessionSavedEquipment(savedDrafts);
      removePendingByTempIds(result.savedTempIds);
      markCategorySaved(currentCategoryId);
    } catch {
      // Error is handled by useSaveEquipment (shows notification)
      return;
    }

    nextStep();
  };

  const handleSkip = () => {
    if (currentStepInfo.categoryId) {
      skipCategory(currentStepInfo.categoryId);
    }
    nextStep();
  };

  const handleBack = () => {
    prevStep();
  };

  const handleFinishReview = () => {
    setShowCompletion(true);
  };

  const handleClose = () => {
    const wasCompletion = showCompletion;
    resetSave();
    setShowCompletion(false);
    onClose();
    if (wasCompletion) {
      onComplete?.();
    }
  };

  const handleGoToEquipment = () => {
    handleClose();
    navigate('/equipment');
  };

  const handleModalClose = () => {
    if (showCompletion) {
      return;
    }

    if (currentCategoryDrafts.length > 0) {
      modals.openConfirmModal({
        title: 'Discard unsaved equipment?',
        centered: true,
        children: (
          <Text size="sm">
            You have unsaved draft equipment in this category. Discard changes and close?
          </Text>
        ),
        labels: { confirm: 'Discard', cancel: 'Keep editing' },
        confirmProps: { color: 'red' },
        onConfirm: () => handleClose(),
      });
      return;
    }

    handleClose();
  };

  // Render step content based on current step
  const renderStepContent = () => {
    if (showCompletion) {
      return (
        <CompletionStep
          savedCount={completionSavedCount}
          onClose={handleClose}
          onGoToEquipment={handleGoToEquipment}
        />
      );
    }

    const stepId = currentStepInfo.id;

    if (stepId === 'welcome') {
      return <WelcomeStep />;
    }

    if (stepId === 'review') {
      return (
        <ReviewStep
          sessionSavedEquipment={state.sessionSavedEquipment}
          onFinish={handleFinishReview}
        />
      );
    }

    // Category steps
    const categoryStepProps = {
      pendingEquipment: state.pendingEquipment,
      onEquipmentChange: setEquipment,
      onManageInEquipment: handleGoToEquipment,
    };

    switch (stepId) {
      case 'circulation':
        return (
          <CirculationStep
            {...categoryStepProps}
            isCategoryComplete={
              state.savedCategories.includes('circulation') ||
              state.preexistingCategories.includes('circulation')
            }
          />
        );
      case 'lighting':
        return (
          <LightingStep
            {...categoryStepProps}
            isCategoryComplete={
              state.savedCategories.includes('lighting') ||
              state.preexistingCategories.includes('lighting')
            }
          />
        );
      case 'filtration':
        return (
          <FiltrationStep
            {...categoryStepProps}
            isCategoryComplete={
              state.savedCategories.includes('filtration') ||
              state.preexistingCategories.includes('filtration')
            }
          />
        );
      case 'control':
        return (
          <ControlStep
            {...categoryStepProps}
            isCategoryComplete={
              state.savedCategories.includes('control') ||
              state.preexistingCategories.includes('control')
            }
          />
        );
      case 'other':
        return (
          <OtherStep
            {...categoryStepProps}
            isCategoryComplete={
              state.savedCategories.includes('other') ||
              state.preexistingCategories.includes('other')
            }
          />
        );
      default:
        return null;
    }
  };

  // Determine button labels based on step
  const getNextButtonLabel = () => {
    if (isFirstStep) return 'Get Started';
    if (currentCategoryId) {
      return currentCategoryDrafts.length > 0 ? 'Save & Continue' : 'Continue';
    }
    return 'Next';
  };

  // Don't show navigation buttons on review (has its own save button) or completion
  const showNavigation = !isLastStep && !showCompletion;
  const showSkipButton = !isFirstStep && !isLastStep;
  const showBackButton = !isFirstStep && !isLastStep;

  return (
    <Modal
      opened={isOpen}
      onClose={handleModalClose}
      title="Set Up Your Equipment"
      size="lg"
      withCloseButton={!showCompletion}
      closeOnEscape={!showCompletion}
      closeOnClickOutside={!showCompletion}
      closeButtonProps={{ 'aria-label': 'Close' }}
    >
      <Stack gap="lg">
      {/* Stepper - hide on completion */}
      {!showCompletion && (
        <Stepper
          active={state.currentStep}
          size="sm"
          styles={{
            steps: { flexWrap: 'wrap' },
          }}
        >
          {WIZARD_STEPS.map((step) => (
            <Stepper.Step key={step.id} label={step.label} />
          ))}
        </Stepper>
      )}

      {/* Step Content */}
      <div data-testid="wizard-step-content">{renderStepContent()}</div>

      {/* Navigation Buttons - not on review or completion */}
      {showNavigation && (
        <Group justify="space-between">
          <Group>
            {showBackButton && (
              <Button variant="subtle" onClick={handleBack}>
                Back
              </Button>
            )}
          </Group>
          <Group>
            {showSkipButton && (
              <Button variant="subtle" onClick={handleSkip}>
                Skip
              </Button>
            )}
            <Button onClick={handleNext} loading={isSaving}>
              {getNextButtonLabel()}
            </Button>
          </Group>
        </Group>
      )}

      {/* Back button on review step */}
      {isLastStep && !showCompletion && (
        <Group justify="flex-start">
          <Button variant="subtle" onClick={handleBack}>
            Back
          </Button>
        </Group>
      )}
      </Stack>
    </Modal>
  );
}

/**
 * Equipment Setup Wizard modal component.
 * Wraps content with WizardProvider for state management.
 */
export function EquipmentSetupWizard({
  isOpen,
  onClose,
  onComplete,
}: EquipmentSetupWizardProps) {
  return (
    <WizardProvider>
      <WizardContent
        isOpen={isOpen}
        onClose={onClose}
        onComplete={onComplete}
      />
    </WizardProvider>
  );
}
